import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatEUR, formatDate, POSTING_CATEGORIES, firstEmail } from "@/lib/constants";

export function PortalLedger() {
  const [ledger, setLedger] = useState(null);
  useEffect(() => { api.get("/portal/ledger").then((r) => setLedger(r.data)); }, []);

  if (!ledger) return <div className="text-sm text-slate-500">Lade...</div>;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Mein Mietkonto</h1>
        <p className="text-sm text-slate-500 mt-1">Übersicht über Soll-, Ist-Buchungen und Saldo</p>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Box label="Soll (Forderung)" value={formatEUR(ledger.soll)} />
        <Box label="Ist (Zahlung)" value={formatEUR(ledger.ist)} accent="text-emerald-700" />
        <Box label="Saldo" value={formatEUR(ledger.balance)} accent={ledger.balance >= 0 ? "text-emerald-700" : "text-rose-700"} highlight />
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <Th>Datum</Th><Th>Typ</Th><Th>Kategorie</Th><Th>Referenz</Th><Th className="text-right">Betrag</Th>
            </tr>
          </thead>
          <tbody>
            {(ledger.postings || []).length === 0 && <tr><td colSpan={5} className="p-6 text-center text-slate-500">Keine Buchungen.</td></tr>}
            {(ledger.postings || []).map((p) => (
              <tr key={p.id} className="border-t border-slate-100">
                <Td className="tabular-nums">{formatDate(p.posting_date)}</Td>
                <Td>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.type === "soll" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"}`}>
                    {p.type === "soll" ? "Soll" : "Ist"}
                  </span>
                </Td>
                <Td>{p.linked_installment_plan_id ? `Ratenzahlung · Rate ${p.linked_installment_index}` : (POSTING_CATEGORIES[p.category] || p.category)}</Td>
                <Td className="text-xs text-slate-600">{p.reference}</Td>
                <Td className={`text-right tabular-nums font-medium ${p.type === "ist" ? "text-emerald-700" : "text-slate-900"}`}>
                  {p.type === "ist" ? "+" : "-"}{formatEUR(p.amount)}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Box({ label, value, accent = "text-slate-900", highlight }) {
  return (
    <div className={`bg-white border rounded-md p-4 ${highlight ? "border-slate-400" : "border-slate-200"}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums mt-1 ${accent}`}>{value}</div>
    </div>
  );
}
function Th({ children, className = "" }) { return <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2.5 ${className}`}>{children}</td>; }


export function PortalDocs() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/portal/documents").then((r) => setItems(r.data)); }, []);
  const download = async (d) => {
    const r = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = d.original_filename || "dokument"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Meine Dokumente</h1>
        <p className="text-sm text-slate-500 mt-1">Vertrag, Abrechnungen, Schriftverkehr</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {items.length === 0 && <div className="p-10 text-center text-sm text-slate-500">Keine Dokumente vorhanden.</div>}
        {items.map((d) => (
          <button key={d.id} onClick={() => download(d)} className="w-full text-left px-5 py-3 border-b border-slate-100 hover:bg-slate-50 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-slate-900">{d.title}</div>
              <div className="text-xs text-slate-500">{d.category} · {formatDate(d.created_at)}</div>
            </div>
            <span className="text-xs text-slate-500">{d.size ? `${Math.round(d.size / 1024)} KB` : ""}</span>
          </button>
        ))}
      </div>
    </div>
  );
}


export function PortalProfile() {
  const [contact, setContact] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/portal/me").then((r) => setContact(r.data.contact)); }, []);

  if (!contact) return <div className="text-sm text-slate-500">Lade...</div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/portal/profile", {
        contact_type: contact.contact_type, first_name: contact.first_name, last_name: contact.last_name,
        emails: contact.emails || [], phones: contact.phones || [],
        address: contact.address || null, roles: contact.roles || [],
      });
      const r = await api.get("/portal/me"); setContact(r.data.contact);
    } catch (e) { /* toast handled elsewhere */ }
    setSaving(false);
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Mein Profil</h1>
        <p className="text-sm text-slate-500 mt-1">Kontaktdaten aktualisieren</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vorname"><Input value={contact.first_name || ""} onChange={(e) => setContact({ ...contact, first_name: e.target.value })} disabled /></Field>
          <Field label="Nachname"><Input value={contact.last_name || ""} disabled /></Field>
        </div>
        <Field label="E-Mail">
          <Input value={firstEmail(contact.emails)} onChange={(e) => setContact({ ...contact, emails: [e.target.value] })} data-testid="portal-profile-email" />
        </Field>
        <Field label="Telefon">
          <Input value={(contact.phones || [""])[0]} onChange={(e) => setContact({ ...contact, phones: [e.target.value] })} data-testid="portal-profile-phone" />
        </Field>
        <button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white text-sm h-9 px-4 rounded-sm" data-testid="portal-profile-save">
          {saving ? "Speichere..." : "Speichern"}
        </button>
      </div>
    </div>
  );
}

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function Field({ label, children }) {
  return <div><Label className="text-xs text-slate-700">{label}</Label><div className="mt-1">{children}</div></div>;
}
